import type {
    B2bCatalog,
    B2bOrder,
    B2bOrderStatus,
    OrderDispatches,
    OrderLineInput,
    OrderQuote,
    PaymentTermTemplate,
    PaymentView,
    B2bShipTo,
} from './b2b';
import type { LoginPayloadResponse } from './consultantInvite';
import { ReparoApi } from './index';

/** The customer portal (/b2b-portal/*): only a B2B customer login can call these. */

export interface PortalMe {
    customerId: string;
    name: string;
    legalName: string | null;
    gstin: string | null;
    segment: string | null;
    shipTos: B2bShipTo[];
    paymentTerms: PaymentTermTemplate[];
    minOrderValue: number;
    orderingOpen: boolean;
}

export interface PortalOrderRow {
    purchaseOrderId: string;
    date: string;
    status: B2bOrderStatus;
    total: number;
    currency: string | null;
    customerReference: string | null;
    expiryDate: string | null;
}

export interface PortalOrderDetail {
    order: B2bOrder;
    payments: PaymentView;
    dispatches: OrderDispatches;
    files: string[];
}

const json = (body: unknown) => JSON.stringify(body);
const order = (id: string) => `/b2b-portal/orders/${encodeURIComponent(id)}`;

/** Resolves for customer logins only; a manufacturer login gets a 403. */
export const getPortalMe = () => ReparoApi<PortalMe>({ method: 'GET', url: '/b2b-portal/me' });
export const getPortalCatalog = () => ReparoApi<B2bCatalog>({ method: 'GET', url: '/b2b-portal/catalog' });
export const quotePortalOrder = (lines: OrderLineInput[], freePicks?: OrderLineInput[]) =>
    ReparoApi<OrderQuote>({ method: 'POST', url: '/b2b-portal/quote', body: json({ lines, freePicks }) });
export const placePortalOrder = (input: {
    lines: OrderLineInput[];
    freePicks?: OrderLineInput[];
    shipToWarehouseId?: string;
    customerReference?: string;
    notes?: string;
}) => ReparoApi<B2bOrder>({ method: 'POST', url: '/b2b-portal/orders', body: json(input) });
/** The portal home's money and tracking: unpaid instalments, rejected proofs to replace, latest orders. */
export interface PortalHomeView {
    dues: Array<{ purchaseOrderId: string; label: string; outstanding: number; dueDate: string | null; dueWhen: string | null; overdue: boolean; verifying: boolean }>;
    rejected: Array<{ purchaseOrderId: string; amount: number; reason: string | null; note: string | null }>;
    recent: Array<{
        purchaseOrderId: string;
        date: string;
        status: string;
        total: number;
        tracking: { transporter: string; trackingNumber: string; dispatchDate: string } | null;
    }>;
}
export const getPortalHome = () => ReparoApi<PortalHomeView>({ method: 'GET', url: '/b2b-portal/home' });
export const listPortalOrders = () => ReparoApi<PortalOrderRow[]>({ method: 'GET', url: '/b2b-portal/orders' });
export const getPortalOrder = (id: string) => ReparoApi<PortalOrderDetail>({ method: 'GET', url: order(id) });
export const confirmPortalPi = (id: string) => ReparoApi<{ ok: boolean }>({ method: 'POST', url: `${order(id)}/confirm-pi` });
export const submitPortalPayment = (
    id: string,
    input: { amount: number; paidOn: string; method: string; reference: string; note?: string },
    proof: File,
) => {
    const body = new FormData();
    body.append('amount', String(input.amount));
    body.append('paidOn', input.paidOn);
    body.append('method', input.method);
    body.append('reference', input.reference);
    if (input.note) body.append('note', input.note);
    body.append('file', proof);
    return ReparoApi<{ duplicateOf: string[] }>({ method: 'POST', url: `${order(id)}/payments`, body });
};
export const receivePortalDispatch = (id: string, stnId: string, items?: Array<{ stnItemId: string; receivedQty: number }>) =>
    ReparoApi<unknown>({ method: 'POST', url: `${order(id)}/dispatches/${stnId}/receive`, body: json({ items }) });
export const getPortalFileUrl = (id: string, key: string) =>
    ReparoApi<{ url: string }>({ method: 'GET', url: `${order(id)}/file?key=${encodeURIComponent(key)}` });
export const getPortalPackingListUrl = (id: string, stnId: string) =>
    ReparoApi<{ url: string }>({ method: 'GET', url: `${order(id)}/dispatches/${stnId}/packing-list` });

/* Invitation (public: the holder has no login yet). */

export interface B2bInviteView {
    email: string;
    firstName: string | null;
    lastName: string | null;
    customerName: string | null;
    brandName: string | null;
    expiresAt: string;
}

export const getB2bInvite = (token: string) =>
    ReparoApi<B2bInviteView>({ method: 'GET', url: `/b2b-invites/${encodeURIComponent(token)}` });
export const acceptB2bInvite = (payload: { token: string; password: string; firstName?: string; lastName?: string }) =>
    ReparoApi<LoginPayloadResponse>({ method: 'POST', url: '/b2b-invites/accept', body: json(payload) });
